// AGC: Profile - User profile page with settings and configurations
// AGC: Accessible via TopNav profile picture click
// AGC: Includes editable fields: state, city (autocomplete), farm name, hectares
// AGC: Supports dark mode with theme selection

import React, { useState, useEffect, useRef } from 'react'
import { APP_VERSION } from '@droidfarm/shared'
import { 
  Settings, 
  LogOut,
  Camera,
  Mail,
  MapPin,
  Building2,
  MessageCircle,
  Check,
  X,
  Edit2,
  Loader2,
  Ruler,
  Trash2,
  ChevronRight,
  // AGC: Bell, Shield, Globe, Moon, Sun, Monitor, HelpCircle, Fingerprint, Languages, Coins - moved to Settings page
} from 'lucide-react'
import { Card, TopBar } from '../shared'
// AGC: TeamManagement import removed - feature not included in current MVP
import { useAuth } from '../../hooks/useAuth'
import { useTranslation } from '../../hooks/useTranslation'
import { updateUserProfile } from '../../services/firebase-auth'
import { searchStates, searchCities, type State, type City } from '../../services/brazil-locations'
// AGC: TIER_DISPLAY_INFO, TIER_PRICING, getNextTier imports removed - Subscription section removed from Profile
import { formatPhoneForDisplay, getPhoneFormatHint, detectCountryFromPhone } from '../../utils/phone'
import { getBackendUrl } from '../../utils/backendUrl'
import type { Screen } from '../../types'

interface ProfileProps {
  onNavigate: (screen: Screen) => void
  onBack: () => void
}

/**
 * AGC: Profile - User profile and settings page with edit functionality
 */
export const Profile: React.FC<ProfileProps> = ({ onBack, onNavigate }) => {
  const { user, logout, getAuthToken } = useAuth()
  
  // AGC: Translation for labels only; language/theme/currency moved to Settings page
  const { t } = useTranslation()
  
  // AGC: User data state - all values come from Firebase, no hardcoded defaults
  const [userData, setUserData] = useState({
    name: '',
    avatar: '',
    email: '',
    whatsappPhone: '',
    state: '',
    city: '',
    farmName: '',
    farmArea: '',
    role: '',
    memberSince: '',
  })
  
  // AGC: Subscription is now managed via SubscriptionContext and SubscriptionPage

  // AGC: Edit states for each field
  const [editingField, setEditingField] = useState<string | null>(null)
  const [editValue, setEditValue] = useState('')
  const [isSaving, setIsSaving] = useState(false)
  const [saveError, setSaveError] = useState('')
  
  // AGC: State and City autocomplete states
  const [stateSearchQuery, setStateSearchQuery] = useState('')
  const [citySearchQuery, setCitySearchQuery] = useState('')
  const [stateSuggestions, setStateSuggestions] = useState<State[]>([])
  const [citySuggestions, setCitySuggestions] = useState<City[]>([])
  const [showStateSuggestions, setShowStateSuggestions] = useState(false)
  const [showCitySuggestions, setShowCitySuggestions] = useState(false)
  const [selectedState, setSelectedState] = useState<State | null>(null)
  const stateInputRef = useRef<HTMLInputElement>(null)
  const cityInputRef = useRef<HTMLInputElement>(null)
  const stateSuggestionsRef = useRef<HTMLDivElement>(null)
  const citySuggestionsRef = useRef<HTMLDivElement>(null)
  
  // AGC: Photo upload ref and state
  const photoInputRef = useRef<HTMLInputElement>(null)
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false)
  const [photoError, setPhotoError] = useState('')

  // AGC: Load user data from Firebase - all data comes from Firebase, no hardcoded values
  useEffect(() => {
    if (user) {
      // AGC: Get display name from Firebase only (no hardcoded fallback)
      const displayName = user.displayName || user.profile?.displayName || ''
      
      // AGC: Format creation date from Firebase only
      const createdAt = user.profile?.createdAt 
        ? new Date(user.profile.createdAt).toLocaleDateString('pt-BR', { month: 'long', year: 'numeric' })
        : ''

      // AGC: Role display names from translations
      const roleDisplay: Record<string, string> = {
        admin: t('profile.roles.admin'),
        manager: t('profile.roles.manager'),
        submanager: t('profile.roles.submanager'),
        user: t('profile.roles.user'),
      }

      // AGC: Farm role display names (for sub-users)
      const farmRoleDisplay: Record<string, string> = {
        owner: t('roleInfo.owner.label') || 'Proprietário',
        manager: t('roleInfo.manager.label') || 'Gerente',
        agronomist: t('roleInfo.agronomist.label') || 'Agrônomo',
        financial: t('roleInfo.financial.label') || 'Financeiro',
        worker: t('roleInfo.worker.label') || 'Operador',
        viewer: t('roleInfo.viewer.label') || 'Visualizador',
      }

      // AGC: Parse location into state and city if it exists
      let parsedState = ''
      let parsedCity = ''
      if (user.profile?.location) {
        const locationParts = user.profile.location.split(',').map((s: string) => s.trim())
        if (locationParts.length >= 2) {
          parsedCity = locationParts[0] ?? ''
          parsedState = locationParts[1] ?? ''
        } else if (locationParts.length === 1) {
          // AGC: Try to determine if it's state or city
          const firstPart = locationParts[0] ?? ''
          if (firstPart.length === 2) {
            parsedState = firstPart
          } else {
            parsedCity = firstPart
          }
        }
      }

      // AGC: Get all profile data from Firebase (no localStorage fallback)
      setUserData({
        name: displayName,
        avatar: user.photoURL || user.profile?.photoURL || '',
        email: user.email || user.profile?.email || '',
        whatsappPhone: user.profile?.whatsappPhone ?? '',
        state: user.profile?.state ?? parsedState,
        city: user.profile?.city ?? parsedCity,
        farmName: user.profile?.farmName ?? '',
        farmArea: user.profile?.farmArea ?? '',
        role: [
          user.profile?.role ? (roleDisplay[user.profile.role] ?? '') : '',
          (user.profile as any)?.farmRole ? (farmRoleDisplay[(user.profile as any).farmRole] ?? '') : '',
        ].filter(Boolean).join(' / '),
        memberSince: createdAt,
      })

      console.log('👤 AGC: Profile loaded from Firebase for user:', displayName || user.email || user.uid)
    }
  }, [user])

  // AGC: Search states when query changes
  useEffect(() => {
    if (editingField === 'state' && stateSearchQuery) {
      const searchStatesAsync = async () => {
        const results = await searchStates(stateSearchQuery)
        setStateSuggestions(results)
        setShowStateSuggestions(true)
      }
      searchStatesAsync()
    } else if (editingField === 'state' && !stateSearchQuery) {
      const loadAllStates = async () => {
        const results = await searchStates('')
        setStateSuggestions(results)
        setShowStateSuggestions(true)
      }
      loadAllStates()
    }
  }, [stateSearchQuery, editingField])

  // AGC: Search cities when query changes and state is selected
  useEffect(() => {
    if (editingField === 'city') {
      // AGC: Get state from selectedState or try to find from userData.state
      const getStateForCity = async () => {
        let stateToUse = selectedState
        
        // AGC: If no selected state but userData has state, try to find it
        if (!stateToUse && userData.state) {
          const states = await searchStates(userData.state)
          if (states.length > 0 && states[0]) {
            stateToUse = states[0]
            setSelectedState(stateToUse)
          }
        }
        
        if (stateToUse) {
          if (citySearchQuery) {
            const results = await searchCities(stateToUse.sigla, citySearchQuery)
            setCitySuggestions(results)
            setShowCitySuggestions(true)
          } else {
            const results = await searchCities(stateToUse.sigla, '')
            setCitySuggestions(results)
            setShowCitySuggestions(true)
          }
        } else {
          setCitySuggestions([])
          setShowCitySuggestions(false)
        }
      }
      
      getStateForCity()
    }
  }, [citySearchQuery, selectedState, editingField, userData.state])

  // AGC: Close suggestions when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (stateSuggestionsRef.current && !stateSuggestionsRef.current.contains(event.target as Node) && 
          stateInputRef.current && !stateInputRef.current.contains(event.target as Node)) {
        setShowStateSuggestions(false)
      }
      if (citySuggestionsRef.current && !citySuggestionsRef.current.contains(event.target as Node) && 
          cityInputRef.current && !cityInputRef.current.contains(event.target as Node)) {
        setShowCitySuggestions(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  /**
   * AGC: Start editing a field
   */
  const startEditing = (field: string, currentValue: string) => {
    setEditingField(field)
    const notInformed = t('profile.notInformed')
    setEditValue(currentValue === notInformed ? '' : currentValue)
    setSaveError('')

    // AGC: Initialize state/city search
    if (field === 'state') {
      setStateSearchQuery(currentValue === notInformed ? '' : currentValue)
      // AGC: FIX - Keep selectedState if it matches current value, otherwise reset
      if (selectedState && selectedState.nome !== currentValue) {
        setSelectedState(null)
      }
    } else if (field === 'city') {
      setCitySearchQuery(currentValue === notInformed ? '' : currentValue)
      // AGC: FIX - Improved state lookup for city editing
      // AGC: Check if selectedState is already set and matches userData.state
      if (selectedState && selectedState.nome === userData.state) {
        // AGC: State already selected, no need to search
        console.log('✅ AGC: Using existing selectedState for city edit:', selectedState.nome)
      } else if (userData.state) {
        // AGC: State not selected but userData has state - search for it
        console.log('🔍 AGC: Searching for state to enable city dropdown:', userData.state)
        searchStates(userData.state).then(states => {
          if (states.length > 0 && states[0]) {
            setSelectedState(states[0])
            console.log('✅ AGC: Found state for city dropdown:', states[0].nome)
          }
        })
      } else {
        // AGC: No state selected or in userData
        setSelectedState(null)
        console.log('⚠️ AGC: No state available for city dropdown')
      }
    }
  }

  /**
   * AGC: Cancel editing
   */
  const cancelEditing = () => {
    setEditingField(null)
    setEditValue('')
    setSaveError('')
    setStateSearchQuery('')
    setCitySearchQuery('')
    setStateSuggestions([])
    setCitySuggestions([])
    setShowStateSuggestions(false)
    setShowCitySuggestions(false)
    setSelectedState(null)
  }

  /**
   * AGC: Save edited field
   */
  const saveField = async (field: string) => {
    if (!user) return

    setIsSaving(true)
    setSaveError('')

    try {
      let valueToSave = editValue.trim()

      // AGC: Validate hectares
      if (field === 'farmArea' && valueToSave) {
        const areaNum = parseFloat(valueToSave.replace(',', '.'))
        if (isNaN(areaNum) || areaNum <= 0) {
          setSaveError(t('profile.errors.invalidHectares'))
          setIsSaving(false)
          return
        }
        valueToSave = `${areaNum.toLocaleString('pt-BR')} ${t('fields.hectares')}`
      }

      // AGC: Handle state and city separately
      const updateData: any = {}
      
      if (field === 'state') {
        // AGC: Use selected state name if available, otherwise use input value
        const stateName = selectedState ? selectedState.nome : valueToSave
        updateData.state = stateName
        // AGC: Also update location field for backward compatibility
        const city = userData.city || ''
        updateData.location = city ? `${city}, ${stateName}` : stateName
        valueToSave = stateName
      } else if (field === 'city') {
        // AGC: Validate that state is selected before saving city
        if (!selectedState && !userData.state) {
          setSaveError(t('profile.selectStateFirst'))
          setIsSaving(false)
          return
        }
        updateData.city = valueToSave
        // AGC: Also update location field for backward compatibility
        const state = selectedState ? selectedState.nome : (userData.state || '')
        updateData.location = state ? `${valueToSave}, ${state}` : valueToSave
      } else if (field === 'whatsappPhone') {
        // AGC: Route through link-phone API to enforce phone uniqueness + send welcome message
        try {
          const backendUrl = getBackendUrl()
          const response = await fetch(`${backendUrl}/api/v1/whatsapp/link-phone`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              userId: user.uid,
              phone: valueToSave,
              userName: userData.name || user.displayName || '',
            }),
          })
          const result = await response.json()
          if (!result.success) {
            setSaveError(result.message)
            setIsSaving(false)
            return
          }
          // AGC: Backend already saved to Firestore — update local state and return early
          const displayPhone = formatPhoneForDisplay(result.phone)
          setUserData(prev => ({ ...prev, whatsappPhone: displayPhone }))
          setEditingField(null)
          setEditValue('')
          setIsSaving(false)
          return
        } catch (e) {
          console.error('❌ AGC: Error linking WhatsApp phone:', e)
          setSaveError(t('profile.errors.saveFirebaseError'))
          setIsSaving(false)
          return
        }
      } else if (field === 'farmName') {
        updateData.farmName = valueToSave
      } else if (field === 'farmArea') {
        updateData.farmArea = valueToSave
      }

      // AGC: Save to Firebase only
      try {
        await updateUserProfile(user.uid, updateData)
        console.log(`✅ AGC: ${field} saved to Firebase:`, valueToSave)
      } catch (e) {
        console.error('❌ AGC: Error updating Firebase profile:', e)
        setSaveError(t('profile.errors.saveFirebaseError'))
        setIsSaving(false)
        return
      }

      // AGC: Update local state
      setUserData(prev => ({
        ...prev,
        [field]: valueToSave || '',
        // AGC: Update location field if state or city changed
        ...(field === 'state' && { location: prev.city ? `${prev.city}, ${valueToSave}` : valueToSave }),
        ...(field === 'city' && { location: prev.state ? `${valueToSave}, ${prev.state}` : valueToSave }),
      }))

      setEditingField(null)
      setEditValue('')
      setStateSearchQuery('')
      setCitySearchQuery('')
      setStateSuggestions([])
      setCitySuggestions([])
      setShowStateSuggestions(false)
      setShowCitySuggestions(false)
      // AGC: FIX - Don't reset selectedState when saving state, preserve it for city dropdown
      // AGC: Only reset selectedState when field is NOT state (e.g., city or other fields)
      if (field !== 'state') {
        setSelectedState(null)
      }
      console.log(`✅ AGC: ${field} saved:`, valueToSave)
    } catch (error) {
      console.error('❌ AGC: Error saving field:', error)
      setSaveError(t('profile.errors.saveError'))
    } finally {
      setIsSaving(false)
    }
  }

  /**
   * AGC: Handle state selection
   */
  const handleStateSelect = (state: State) => {
    setSelectedState(state)
    setEditValue(state.nome)
    setStateSearchQuery(state.nome)
    setShowStateSuggestions(false)
    // AGC: Clear city when state changes
    setCitySearchQuery('')
    setCitySuggestions([])
  }

  /**
   * AGC: Handle city selection
   */
  const handleCitySelect = (city: City) => {
    setEditValue(city.nome)
    setCitySearchQuery(city.nome)
    setShowCitySuggestions(false)
  }

  /**
   * AGC: Handle photo upload for non-Google users
   * AGC: Converts image to base64 for storage (can be upgraded to Firebase Storage later)
   */
  const handlePhotoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0]
    if (!file || !user) return

    setPhotoError('')
    setIsUploadingPhoto(true)

    try {
      // AGC: Validate file type
      if (!file.type.startsWith('image/')) {
        setPhotoError(t('profile.errors.invalidImageType') || 'Please select an image file')
        setIsUploadingPhoto(false)
        return
      }

      // AGC: Validate file size (max 5MB)
      const maxSize = 5 * 1024 * 1024 // 5MB
      if (file.size > maxSize) {
        setPhotoError(t('profile.errors.imageTooLarge') || 'Image too large (max 5MB)')
        setIsUploadingPhoto(false)
        return
      }

      // AGC: Resize image if needed for storage optimization
      const resizedDataUrl = await resizeImage(file, 400, 400) // 400x400 max dimensions

      // AGC: Update local state immediately for preview
      setUserData(prev => ({ ...prev, avatar: resizedDataUrl }))

      // AGC: Save to Firebase
      await updateUserProfile(user.uid, { photoURL: resizedDataUrl })
      console.log('✅ AGC: Avatar uploaded successfully')
    } catch (error) {
      console.error('❌ AGC: Error uploading avatar:', error)
      setPhotoError(t('profile.errors.saveError') || 'Error uploading photo')
    } finally {
      setIsUploadingPhoto(false)
      // AGC: Reset file input
      if (photoInputRef.current) {
        photoInputRef.current.value = ''
      }
    }
  }

  /**
   * AGC: Helper function to resize image
   */
  const resizeImage = (file: File, maxWidth: number, maxHeight: number): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = (e) => {
        const img = new Image()
        img.onload = () => {
          // AGC: Calculate new dimensions while maintaining aspect ratio
          let width = img.width
          let height = img.height

          if (width > maxWidth) {
            height = (height * maxWidth) / width
            width = maxWidth
          }
          if (height > maxHeight) {
            width = (width * maxHeight) / height
            height = maxHeight
          }

          // AGC: Create canvas and draw resized image
          const canvas = document.createElement('canvas')
          canvas.width = width
          canvas.height = height
          const ctx = canvas.getContext('2d')
          if (!ctx) {
            reject(new Error('Failed to get canvas context'))
            return
          }
          ctx.drawImage(img, 0, 0, width, height)

          // AGC: Convert to base64 with JPEG compression
          const dataUrl = canvas.toDataURL('image/jpeg', 0.8)
          resolve(dataUrl)
        }
        img.onerror = () => reject(new Error('Failed to load image'))
        img.src = e.target?.result as string
      }
      reader.onerror = () => reject(new Error('Failed to read file'))
      reader.readAsDataURL(file)
    })
  }

  // AGC: Biometric and app settings moved to Configurações page (left menu)

  // AGC: State for reset data loading
  const [isResettingData, setIsResettingData] = useState(false)

  /**
   * AGC: Reset all user data to start fresh (shows wizard again)
   */
  const handleResetAllData = async () => {
    if (!user) return

    const confirmed = confirm(
      '⚠️ ATENÇÃO: Esta ação irá apagar TODOS os seus dados:\n\n' +
      '• Fazendas e talhões\n' +
      '• Tarefas\n' +
      '• Despesas\n' +
      '• Estoque\n' +
      '• Notificações\n\n' +
      'Esta ação NÃO PODE ser desfeita!\n\n' +
      'Deseja continuar?'
    )

    if (!confirmed) return

    setIsResettingData(true)

    try {
      const token = await getAuthToken()
      const backendUrl = getBackendUrl()
      
      const response = await fetch(`${backendUrl}/api/v1/auth/reset-all-data`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })

      if (response.ok) {
        // AGC: Clear ALL localStorage data AGGRESSIVELY to ensure fresh start
        // AGC: First, collect all keys that need to be removed
        const keysToRemove: string[] = []
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i)
          if (key && key.startsWith('droidfarm_')) {
            keysToRemove.push(key)
          }
        }
        
        // AGC: Log what we're about to clear
        console.log('🧹 AGC: Clearing these localStorage keys:', keysToRemove)
        
        // AGC: Remove each key individually
        keysToRemove.forEach(key => {
          localStorage.removeItem(key)
          console.log(`   🗑️ Removed: ${key}`)
        })
        
        // AGC: Double-check by clearing any remaining droidfarm keys (in case iteration missed some)
        let remaining = []
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i)
          if (key && key.startsWith('droidfarm_')) {
            remaining.push(key)
          }
        }
        if (remaining.length > 0) {
          console.warn('⚠️ AGC: Found remaining keys after first pass:', remaining)
          remaining.forEach(key => localStorage.removeItem(key))
        }
        
        console.log('✅ AGC: Cleared', keysToRemove.length, 'localStorage keys')
        
        // AGC: Set a flag to indicate fresh start (will be checked on reload)
        sessionStorage.setItem('droidfarm_fresh_start', 'true')
        
        alert('✅ Dados resetados com sucesso!\n\nO app será recarregado para mostrar o assistente de configuração.')
        
        // AGC: Force full page reload with cache bypass
        window.location.href = window.location.origin + window.location.pathname + '?reset=' + Date.now()
      } else {
        const data = await response.json()
        alert(`❌ Erro ao resetar dados: ${data.message || 'Tente novamente'}`)
      }
    } catch (error) {
      console.error('❌ AGC: Error resetting data:', error)
      alert('❌ Erro ao conectar com o servidor. Verifique sua conexão.')
    } finally {
      setIsResettingData(false)
    }
  }

  /**
   * AGC: Editable field component with autocomplete for state and city
   */
  const EditableField: React.FC<{
    field: string
    icon: React.ReactNode
    label: string
    value: string
    placeholder?: string
    type?: string
    isAutocomplete?: boolean
    helperText?: string
  }> = ({ field, icon, label, value, placeholder, type = 'text', isAutocomplete = false, helperText }) => {
    const isEditing = editingField === field
    const displayValue = value || t('profile.notInformed')

    if (isEditing && isAutocomplete) {
      // AGC: Autocomplete field for state or city
      const isState = field === 'state'
      const searchQuery = isState ? stateSearchQuery : citySearchQuery
      const suggestions = isState ? stateSuggestions : citySuggestions
      const showSuggestions = isState ? showStateSuggestions : showCitySuggestions
      const inputRef = isState ? stateInputRef : cityInputRef
      const suggestionsRef = isState ? stateSuggestionsRef : citySuggestionsRef

      return (
        <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800/50">
          <div className="flex items-center gap-3 mb-2">
            {icon}
            <label className="text-xs font-medium text-gray-500 dark:text-gray-400">{label}</label>
          </div>
          <div className="flex gap-2 relative">
            <div className="flex-1 relative">
              <input
                ref={inputRef}
                type="text"
                value={searchQuery}
                onChange={(e) => {
                  const query = e.target.value
                  if (isState) {
                    setStateSearchQuery(query)
                    setEditValue(query)
                  } else {
                    setCitySearchQuery(query)
                    setEditValue(query)
                  }
                  setSaveError('')
                }}
                onFocus={() => {
                  if (isState) {
                    setShowStateSuggestions(true)
                  } else {
                    setShowCitySuggestions(true)
                  }
                }}
                placeholder={placeholder || `${t('profile.enter')} ${label.toLowerCase()}`}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-lg text-sm focus:ring-2 focus:ring-[#0B5F4A] dark:focus:ring-emerald-500 focus:border-[#0B5F4A] dark:focus:border-emerald-500"
                autoFocus
              />
              {/* AGC: Suggestions dropdown */}
              {showSuggestions && suggestions.length > 0 && (
                <div
                  ref={suggestionsRef}
                  className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-lg shadow-lg max-h-60 overflow-y-auto"
                >
                  {suggestions.map((item) => (
                    <button
                      key={isState ? (item as State).id : (item as City).id}
                      onClick={() => {
                        if (isState) {
                          handleStateSelect(item as State)
                        } else {
                          handleCitySelect(item as City)
                        }
                      }}
                      className="w-full px-3 py-2 text-left text-sm text-gray-900 dark:text-gray-100 hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors border-b border-gray-100 dark:border-gray-700 last:border-b-0"
                    >
                      {isState ? (item as State).nome : (item as City).nome}
                      {isState && (
                        <span className="ml-2 text-xs text-gray-500 dark:text-gray-400">({(item as State).sigla})</span>
                      )}
                    </button>
                  ))}
                </div>
              )}
              {/* AGC: Helper message for city field when no state selected */}
              {!isState && !selectedState && !userData.state && (
                <p className="text-xs text-amber-600 dark:text-amber-400 mt-1">
                  {t('profile.selectStateFirstForCities')}
                </p>
              )}
            </div>
            <button
              onClick={() => saveField(field)}
              disabled={isSaving}
              className="px-3 py-2 bg-[#0B5F4A] dark:bg-emerald-600 text-white rounded-lg hover:bg-[#094d3d] dark:hover:bg-emerald-500 disabled:opacity-50 transition-colors"
            >
              {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            </button>
            <button
              onClick={cancelEditing}
              className="px-3 py-2 bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          {saveError && <p className="text-xs text-red-500 dark:text-red-400 mt-1">{saveError}</p>}
        </div>
      )
    }

    if (isEditing) {
      // AGC: Regular text input field
      return (
        <div className="px-4 py-3 bg-gray-50 dark:bg-gray-800/50">
          <div className="flex items-center gap-3 mb-2">
            {icon}
            <label className="text-xs font-medium text-gray-500 dark:text-gray-400">{label}</label>
          </div>
          <div className="flex gap-2">
            <div className="flex-1 relative">
              <input
                type={type}
                value={editValue}
                onChange={(e) => {
                  setEditValue(e.target.value)
                  setSaveError('')
                }}
                placeholder={placeholder || `${t('profile.enter')} ${label.toLowerCase()}`}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 rounded-lg text-sm focus:ring-2 focus:ring-[#0B5F4A] dark:focus:ring-emerald-500 focus:border-[#0B5F4A] dark:focus:border-emerald-500"
                autoFocus
              />
              {/* AGC: Helper text for field guidance */}
              {helperText && (
                <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{helperText}</p>
              )}
            </div>
            <button
              onClick={() => saveField(field)}
              disabled={isSaving}
              className="px-3 py-2 bg-[#0B5F4A] dark:bg-emerald-600 text-white rounded-lg hover:bg-[#094d3d] dark:hover:bg-emerald-500 disabled:opacity-50 transition-colors"
            >
              {isSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
            </button>
            <button
              onClick={cancelEditing}
              className="px-3 py-2 bg-gray-200 dark:bg-gray-700 text-gray-600 dark:text-gray-300 rounded-lg hover:bg-gray-300 dark:hover:bg-gray-600 transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
          {saveError && <p className="text-xs text-red-500 dark:text-red-400 mt-1">{saveError}</p>}
        </div>
      )
    }

    return (
      <div className="flex items-center gap-3 px-4 py-3">
        {icon}
        <div className="flex-1">
          <p className="text-xs text-gray-500 dark:text-gray-400">{label}</p>
          <p className={`text-sm font-medium ${value ? 'text-gray-900 dark:text-gray-100' : 'text-gray-400 dark:text-gray-500'}`}>
            {displayValue}
          </p>
        </div>
        <button
          onClick={() => startEditing(field, value)}
          className="p-2 text-gray-400 dark:text-gray-500 hover:text-[#0B5F4A] dark:hover:text-emerald-400 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg opacity-100 transition-all"
          aria-label={`Editar ${label}`}
        >
          <Edit2 className="w-4 h-4" />
        </button>
      </div>
    )
  }

  return (
    // AGC: AppHeader (rendered by MainMenu above) already handles safe-area-top, and main
    // AGC: handles safe-area-bottom — do NOT add redundant safe-area padding here or the
    // AGC: profile page ends up with a visible gap between AppHeader and the green TopBar.
    <div className="min-h-screen bg-[#F4F5F7] dark:bg-gray-900 transition-colors">
      <TopBar
        title={t('profile.title')}
        onBack={onBack}
        actions={
          <button
            onClick={() => onNavigate('settings')}
            className="p-2 rounded-full hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors"
            aria-label={t('common.settings')}
          >
            <Settings className="w-5 h-5 text-gray-600 dark:text-gray-300" />
          </button>
        }
      />

      {/* AGC: Profile Header */}
      <div className="bg-white dark:bg-gray-800 px-4 py-6 border-b border-gray-200 dark:border-gray-700 transition-colors">
        <div className="flex items-center gap-4">
          <div className="relative">
            {userData.avatar ? (
              <img src={userData.avatar} alt={userData.name} className="w-20 h-20 rounded-full object-cover border-3 border-[#0B5F4A] dark:border-emerald-500" />
            ) : (
              <div className="w-20 h-20 rounded-full bg-[#0B5F4A] dark:bg-emerald-600 flex items-center justify-center">
                <span className="text-white font-bold text-2xl">{userData.name.charAt(0).toUpperCase()}</span>
              </div>
            )}
            {/* AGC: Hidden file input for photo upload */}
            <input
              type="file"
              ref={photoInputRef}
              accept="image/*"
              onChange={handlePhotoUpload}
              className="hidden"
            />
            {/* AGC: Camera button - triggers file input */}
            <button 
              onClick={() => photoInputRef.current?.click()}
              disabled={isUploadingPhoto}
              className="absolute bottom-0 right-0 w-7 h-7 bg-[#0B5F4A] dark:bg-emerald-600 rounded-full flex items-center justify-center border-2 border-white dark:border-slate-800 shadow-sm hover:bg-[#094d3d] dark:hover:bg-emerald-500 transition-colors disabled:opacity-50"
            >
              {isUploadingPhoto ? (
                <Loader2 className="w-3.5 h-3.5 text-white animate-spin" />
              ) : (
                <Camera className="w-3.5 h-3.5 text-white" />
              )}
            </button>
          </div>
          <div className="flex-1">
            <h2 className="text-xl font-bold text-gray-900 dark:text-gray-50">{userData.name}</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400">{userData.role}</p>
            <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">{t('profile.memberSince')} {userData.memberSince}</p>
            {/* AGC: Photo upload error message */}
            {photoError && (
              <p className="text-xs text-red-500 dark:text-red-400 mt-1">{photoError}</p>
            )}
          </div>
        </div>
      </div>

      {/* AGC: Contact Info (Editable) */}
      <div className="px-4 mt-4">
        <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">{t('profile.personalInfo')}</h3>
        <Card padding="none">
          <div className="divide-y divide-gray-100">
            <div className="flex items-center gap-3 px-4 py-3">
              <Mail className="w-5 h-5 text-gray-400 dark:text-gray-500" />
              <div className="flex-1">
                <p className="text-xs text-gray-500 dark:text-gray-400">{t('profile.email')}</p>
                <p className="text-sm font-medium text-gray-900 dark:text-gray-100">{userData.email || t('profile.notInformed')}</p>
              </div>
            </div>
            <EditableField field="state" icon={<MapPin className="w-5 h-5 text-gray-400" />} label={t('profile.state')} value={userData.state} placeholder={t('profile.state')} isAutocomplete={true} />
            <EditableField field="city" icon={<MapPin className="w-5 h-5 text-gray-400" />} label={t('profile.city')} value={userData.city} placeholder={t('profile.city')} isAutocomplete={true} />
          </div>
        </Card>
      </div>

      {/* AGC: WhatsApp Integration */}
      <div className="px-4 mt-4">
        <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-2">{t('profile.whatsappVoiceCommands')}</h3>
        <Card padding="none">
          <div className="p-4">
            <div className="flex items-start gap-3 p-3 bg-green-50 dark:bg-green-900/20 rounded-lg mb-4">
              <MessageCircle className="w-5 h-5 text-green-600 dark:text-green-400 mt-0.5 flex-shrink-0" />
              <div className="text-xs text-green-700 dark:text-green-300">
                <p className="font-medium mb-1">{t('profile.sendVoiceCommands')}</p>
                <p>{t('profile.linkWhatsAppNumber')}</p>
              </div>
            </div>
            <EditableField field="whatsappPhone" icon={<MessageCircle className="w-5 h-5 text-green-500" />} label={t('profile.whatsapp')} value={userData.whatsappPhone} placeholder={getPhoneFormatHint(detectCountryFromPhone(userData.whatsappPhone))} type="tel" helperText={t('profile.whatsappPhoneHelperText')} />
          </div>
        </Card>
      </div>

      {/* AGC: Farm Info removed from profile - managed via Farm Settings */}

      {/* AGC: Configurações (biometric, language, theme, security, etc.) moved to Configurações in left menu */}

      {/* AGC: Danger Zone - Reset Data */}
      <div className="px-4 mt-4">
        <h3 className="text-sm font-semibold text-red-500 dark:text-red-400 uppercase tracking-wide mb-2">
          {t('profile.dangerZone') || 'Zona de Perigo'}
        </h3>
        <Card padding="none">
          <button 
            onClick={handleResetAllData}
            disabled={isResettingData}
            className="w-full flex items-center gap-3 px-4 py-3 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors text-left"
          >
            <div className="w-10 h-10 rounded-lg bg-red-100 dark:bg-red-900/30 flex items-center justify-center text-red-600 dark:text-red-400">
              {isResettingData ? <Loader2 className="w-5 h-5 animate-spin" /> : <Trash2 className="w-5 h-5" />}
            </div>
            <div className="flex-1">
              <p className="text-sm font-medium text-red-600 dark:text-red-400">
                {t('profile.resetAllData') || 'Resetar Todos os Dados'}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {t('profile.resetAllDataDescription') || 'Apaga fazendas, talhões, tarefas e estoque'}
              </p>
            </div>
            <ChevronRight className="w-5 h-5 text-red-400 dark:text-red-500" />
          </button>
        </Card>
      </div>

      {/* AGC: Logout Button */}
      <div className="px-4 mt-4 pb-24">
        <button 
          onClick={async () => {
            if (confirm(t('auth.logoutSuccess') + '?')) {
              await logout()
            }
          }}
          className="w-full flex items-center justify-center gap-2 py-3 px-4 bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 rounded-xl border border-red-200 dark:border-red-800 hover:bg-red-100 dark:hover:bg-red-900/30 transition-colors"
        >
          <LogOut className="w-5 h-5" />
          <span className="font-medium">{t('common.logout')}</span>
        </button>
      </div>

      {/* AGC: Language, currency, theme and accent modals moved to Configurações page */}

      {/* AGC: Frontend version at bottom — reads from root package.json via APP_VERSION */}
      <div className="mt-8 pt-6 border-t border-gray-200 dark:border-gray-700">
        <div className="text-center text-xs text-gray-500 dark:text-gray-400">
          <span>DroidFarm v{APP_VERSION}</span>
          <span className="mx-2">•</span>
          <span>{new Date().getFullYear()}</span>
        </div>
      </div>
    </div>
  )
}

export default Profile
